import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// In-memory access token. Sent as an Authorization: Bearer header on every
// request. Kept out of localStorage/cookies on purpose: an injected script
// can't read it, and a cross-site request can't attach it — so API calls
// aren't vulnerable to CSRF. It's lost on reload and re-obtained from the
// httpOnly refresh cookie via /auth/refresh (see AuthContext bootstrap).
let accessToken = null;
export function setAccessToken(t) { accessToken = t || null; }
export function getAccessToken() { return accessToken; }

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  // Needed so the httpOnly refresh cookie rides along on /auth/refresh; API
  // auth itself is the Bearer header, not the cookie.
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach the Bearer token to every outgoing request when we have one.
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// If a protected request returns 401, exchange the refresh cookie for a fresh
// access token once, then retry. When refresh itself fails, broadcast an
// auth-expired event so AuthContext drops the session and the route guards
// redirect to /login (instead of pages hanging on their loading skeletons).
export const AUTH_EXPIRED_EVENT = "vtx:auth-expired";
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    if (error.response?.status === 401 && !original._retry && !original.url?.includes("/auth/")) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = api.post("/auth/refresh").then(({ data }) => {
            setAccessToken(data.access_token);
            return data;
          });
        }
        await refreshing;
        refreshing = null;
        return api(original); // request interceptor re-adds the new Bearer token
      } catch (e) {
        refreshing = null;
        setAccessToken(null);
        if (typeof window !== "undefined") window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
      }
    }
    return Promise.reject(error);
  }
);

// Auth'd file endpoints (invoice PDF, CSV export) can no longer be reached by a
// plain <a href> / new-tab navigation, since a browser navigation can't send the
// Bearer header. Fetch them through `api` (which adds the header) as a blob.

// Open a blob in a new tab (e.g. view a PDF). The blank tab is opened
// synchronously inside the click handler so the pop-up blocker allows it, then
// navigated to the blob once fetched.
export async function openAuthedBlob(path) {
  const win = window.open("about:blank", "_blank");
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (win) win.location = url;
    else {
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noreferrer";
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    throw e;
  }
}

// Download a blob to disk (e.g. CSV export).
export async function downloadAuthedBlob(path, filename) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "download";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
